import { ArrowLeft, ArrowRight, Trash } from 'lucide-react';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { addLanguage, deleteLanguage } from '../../redux/Slices/ResumeSlice';
import Button from '../Common/Button';

type DropdownOption = { label: string; value: string };

type Props = {
  step: number;
  totalSteps: number;
  stepName: string;
  prevStepName: string;
  onNext: () => void;
  onBack: () => void;
  dropdowns?: {
    proficiency?: DropdownOption[];
  };
};

type Language = {
  name: string;
  proficiency: string;
};

const LanguagesForm: React.FC<Props> = ({
  step,
  totalSteps,
  stepName,
  prevStepName,
  onNext,
  onBack,
  dropdowns
}) => {
  const dispatch = useDispatch();

  const languages: Language[] = useSelector(
    (state: any) => state.root.resume.languages
  );

  const LANGUAGE_LIMIT = 50;

  const [form, setForm] = useState({
    name: '',
    proficiency: ''
  });

  const [errors, setErrors] = useState({
    name: '',
    proficiency: ''
  });

  /* changed to normal function */
  function CharCount(value: string) {
    const len = value?.length || 0;

    return (
      <span
        className={`text-xs ${
          len >= LANGUAGE_LIMIT ? 'text-red-500' : 'text-gray-400'
        }`}
      >
        {len}/{LANGUAGE_LIMIT}
      </span>
    );
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;

    if (name === 'name') {
      const alphabetRegex = /^[A-Za-z ]*$/;

      if (!alphabetRegex.test(value)) {
        setErrors(prev => ({
          ...prev,
          name: 'Only alphabets allowed'
        }));

        return;
      }

      if (value.length > LANGUAGE_LIMIT) return;
    }

    setForm({
      ...form,
      [name]: value
    });

    let message = '';

    if (name === 'name' && !value.trim()) {
      message = 'Language is required';
    }

    if (name === 'name' && value.length === LANGUAGE_LIMIT) {
      message = 'Max 50 characters reached';
    }

    if (name === 'proficiency' && !value) {
      message = 'Proficiency is required';
    }

    setErrors(prev => ({
      ...prev,
      [name]: message
    }));
  };

  const isSaveValid =
    form.name?.trim() && form.proficiency && form.name.length <= LANGUAGE_LIMIT;

  const saveLanguage = () => {
    if (!isSaveValid) return;

    dispatch(addLanguage(form));

    setForm({
      name: '',
      proficiency: 'BASIC'
    });

    setErrors({
      name: '',
      proficiency: ''
    });
  };

  return (
    <div className='w-full bg-gamma'>
      <div className='flex-1 p-10'>
        <div className='mb-4 flex justify-between'>
          <div>
            <p className='text-sm text-gray-500'>
              Step {step} of {totalSteps}
            </p>

            <h1 className='text-2xl font-semibold'>{stepName}</h1>

            <p className='mt-1 text-sm text-gray-400'>
              Specify languages and proficiency
            </p>
          </div>

          <div className='text-brand'>
            {Math.round(((step - 1) / totalSteps) * 100)} %
          </div>
        </div>

        <div className='mb-8 h-1 rounded bg-gray-200'>
          <div
            className='h-1 rounded bg-brand'
            style={{
              width: `${((step - 1) / totalSteps) * 100}%`
            }}
          />
        </div>

        <div className='rounded-xl bg-white p-6 shadow-sm'>
          <h3 className='mb-4 font-semibold'>+ Add New Language</h3>

          <div className='grid grid-cols-2 gap-4'>
            <div>
              <div className='flex items-center justify-between'>
                <label className='text-sm font-medium'>
                  Language <span className='text-red-500'>*</span>
                </label>

                {CharCount(form.name)}
              </div>

              <input
                name='name'
                maxLength={LANGUAGE_LIMIT}
                value={form.name}
                onChange={handleChange}
                placeholder='Enter language'
                className={`mt-1 w-full rounded-lg border p-3 focus:outline-none
        ${errors.name ? 'border-red-500' : 'border-gray-300'}
        `}
              />

              {errors.name && (
                <p className='mt-1 text-xs text-red-500'>{errors.name}</p>
              )}
            </div>

            <div>
              <label className='text-sm font-medium'>
                Proficiency <span className='text-red-500'>*</span>
              </label>

              <select
                name='proficiency'
                value={form.proficiency}
                onChange={handleChange}
                className={`mt-1 w-full rounded-lg border p-3 focus:outline-none
        ${errors.proficiency ? 'border-red-500' : 'border-gray-300'}
        `}
              >
                <option value=''>Select proficiency</option>

                {dropdowns?.proficiency?.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>

              {errors.proficiency && (
                <p className='mt-1 text-xs text-red-500'>
                  {errors.proficiency}
                </p>
              )}
            </div>
          </div>

          <Button
            label='+ Save Language'
            handleClickBtn={saveLanguage}
            variant='contained'
            disabled={!isSaveValid}
            classes='mt-5'
          />
        </div>

        <div className='mt-6 rounded-xl bg-white p-6 shadow-sm'>
          <h3 className='mb-4 font-semibold'>Added Languages</h3>

          {languages.length === 0 ? (
            <p className='text-sm text-gray-400'>No languages added yet.</p>
          ) : (
            <div className='space-y-3'>
              {languages.map((lang: Language) => (
                <div
                  key={lang.name}
                  className='flex items-center justify-between rounded-lg border p-3'
                >
                  <div>
                    <p className='font-medium'>{lang.name}</p>

                    <p className='text-xs text-gray-500'>{lang.proficiency}</p>
                  </div>

                  <button
                    onClick={() => dispatch(deleteLanguage(lang.name))}
                    className='text-xs text-red-500'
                  >
                    <Trash size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className='mt-10 flex items-center justify-between'>
          <Button
            label={`Previous: ${prevStepName}`}
            handleClickBtn={onBack}
            variant='outlined'
          >
            <ArrowLeft size={16} />
          </Button>

          <Button
            label='Continue'
            handleClickBtn={onNext}
            variant='contained'
            iconPosition='right'
          >
            <ArrowRight size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LanguagesForm;
